import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_FORMAT,
  TypeGuard,
  toTimestamp,
  type Timestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { flow, isBoolean, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { PraktikaTransationType } from './appointment-invoice-payment';

export interface IPraktikaAppointmentInvoiceAdjustment {
  id: number;
  appointment_id: number;
  type_id: PraktikaTransationType;
  description: string;
  effective_date: string; // '2020-02-24'
  is_percent_from_total: boolean;
  percent_value: number | string | null; // '10000'
  amount: number;
  notes: string;
  deposit_transfer_source_id: number | null;

  // entryType: PraktikaInvoiceAddType.Adjustment;
  // effect: PraktikaTransactionEffect;
  // createdDate: string; // '2020-02-24 15:37:34'
  // transactionId: number;
  // isCombined: boolean;
}

export function isPraktikaAppointmentInvoiceAdjustment(
  item: unknown,
  logError: boolean
): item is IPraktikaAppointmentInvoiceAdjustment {
  return TypeGuard.interface<IPraktikaAppointmentInvoiceAdjustment>(
    {
      id: isNumber,
      appointment_id: isNumber,
      type_id: TypeGuard.enumValue(PraktikaTransationType),
      description: isString,
      effective_date: isString, // '2020-02-24'
      is_percent_from_total: isBoolean,
      percent_value: TypeGuard.nilOr([isString, isNumber]), // '10000'
      amount: isNumber,
      notes: isString,
      deposit_transfer_source_id: TypeGuard.nilOr(isNumber),
    },
    logError
  )(item);
}

export const PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_RESOURCE_TYPE =
  'patientAppointmentInvoiceAdjustment';

export const PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Appointment Invoice Adjustment List',
      description: '',
      idPrefix: PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface IPraktikaAppointmentInvoiceAdjustmentTranslations {
  effectiveDate: Timestamp;
}

export interface IPraktikaAppointmentInvoiceAdjustmentFilters {
  appointmentId: string;
  effectiveDate: Timestamp;
}

const PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_SOURCE_QUERY = `SELECT
  convert_to_integer(iAppointmentId) as appointment_id,
  convert_to_integer(iAdjustmentId) as id,
  convert_to_integer(iAdjustmentType) as type_id,
  vchDesc as description,
  dtEffectiveDate as effective_date,
  convert_to_boolean(bPercentFromTotal) as is_percent_from_total,
  convert_to_integer(iPercentValue) as percent_value,
  convert_to_integer(iAmount) as amount,
  quote_for_export as notes,
  convert_to_integer(iDepositTransferSourceId) as deposit_transfer_source_id
FROM appointment_adjustments
ORDER BY id DESC
`;

export type PraktikaAppointmentInvoiceAdjustment = ISourceEntityTypes<
  IPraktikaAppointmentInvoiceAdjustment,
  IPraktikaAppointmentInvoiceAdjustmentTranslations,
  IPraktikaAppointmentInvoiceAdjustmentFilters
>;

export class PatientAppointmentInvoiceAdjustmentSourceEntity extends BaseSourceEntity<PraktikaAppointmentInvoiceAdjustment> {
  sourceEntity = PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_SOURCE_ENTITY;
  entityResourceType = PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_RESOURCE_TYPE;
  sourceQuery = PATIENT_APPOINTMENT_INVOICE_ADJUSTMENT_SOURCE_QUERY;
  verifySourceFn = isPraktikaAppointmentInvoiceAdjustment;
  override transformDataFn = flow([]);

  translate(
    note: IPraktikaAppointmentInvoiceAdjustment,
    timezone: Timezone
  ): IPraktikaAppointmentInvoiceAdjustmentTranslations {
    return {
      effectiveDate: toTimestamp(
        moment.tz(note.effective_date, ISO_DATE_FORMAT, timezone).startOf('day')
      ),
    };
  }

  getSourceRecordId(data: IPraktikaAppointmentInvoiceAdjustment): number {
    return data.id;
  }

  getSourceLabel(data: IPraktikaAppointmentInvoiceAdjustment): string {
    return `${data.id} ${data.appointment_id}`;
  }

  getFilterData(
    data: IPraktikaAppointmentInvoiceAdjustment,
    timezone: Timezone
  ): IPraktikaAppointmentInvoiceAdjustmentFilters {
    return {
      appointmentId: data.appointment_id.toString(),
      effectiveDate: toTimestamp(
        moment.tz(data.effective_date, ISO_DATE_FORMAT, timezone).startOf('day')
      ),
    };
  }
}
